import { call, put, all, takeLatest, select, takeEvery } from 'redux-saga/effects'
import {
  INIT,
  SET_ERROR,
  AUTH_SUCCESS,
  AUTH_ERROR,
  LOCATION_CHANGE,
  FETCH_CONTACT,
  UPDATE_CONTACT,
  SET_CONTACT,
  SAVE_CONTACT,
  SAVE_SUBSCRIPTION,
  FETCH_CONTACTS,
  UPDATE_CONTACTS,
  SET_CONTACTS,
  SAVE_DEACTIVATE,
  SAVE_EMAIL,
  ADD_CONTACTS,
  CONFIRM_PROFILE,
  FETCH_EXCLUSIONS,
  SET_EXCLUSIONS,
  SAVE_EXCLUSIONS,
  FETCH_EMAIL_PREFERENCES,
  UPDATE_EMAIL_PREFERENCES,
  SET_EMAIL_PREFERENCES,
  SAVE_EMAIL_PREFERENCES,
  FETCH_DATABASE_PREFERENCES,
  SAVE_DATABASE_PREFERENCES,
  SET_DATABASE_PREFERENCES,
  SET_FILTER_RESULTS,
  FETCH_FILTER_RESULTS,
  DISMISS_FIRST_TIME_HELP
} from './actions'
import {
  fetchJSON,
  getData,
  getLocalToken,
  getServerToken,
  checkIsAdmin,
  putData,
  deleteData,
  postData
} from '../libs/api'
import jwt from 'jsonwebtoken'
import { getReturnUrlDomain, getReturnUrlPath, serializeError } from '../libs/utils'
import settings from '../libs/settings'
import { DEFAULT_EMAIL_PREFERENCE } from '../libs/email-preferences'
import Raven from 'raven-js'
import { isEmpty } from 'lodash'
import { getUserInfo } from './selectors'
import { TOKEN } from '../libs/permissions'

function * authorize (action) {
  let token = getLocalToken()

  try {
    if (!token) {
      const response = yield call(getServerToken)
      token = response.token
    }
    window.sessionStorage.setItem(TOKEN, token)
    const decodedToken = jwt.decode(token)
    const isAdmin = yield call(checkIsAdmin)
    const userName = decodedToken.sub
    const emailAddress = decodedToken.email
    const claims = decodedToken['http://secure.ipdanalytics.com/claims/subscriberPermission'] || []
    yield put({ type: AUTH_SUCCESS, payload: {token, isAdmin, userName, emailAddress, claims} })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: AUTH_ERROR, payload: error })
  }
}

function confirmErrorHandler (error, nextUrl) {
  if (settings.isProduction) Raven.captureException(serializeError(error))
  console.log(error)
  const msg = `there was an error (check your logs for more details!)\nin production we'll just breeze on to ${nextUrl}\nclick OK to go there now, or Cancel to hang out here`
  if (settings.isProduction || window.confirm(msg)) {
    window.location.href = nextUrl
  }
}

function * fetchContact (action) {
  try {
    const [contact, subscription] = yield all([
      call(getData, `contacts/${action.payload.emailAddress}`),
      call(getData, `subscriptions/${action.payload.emailAddress}`)
    ])
    yield put({ type: SET_CONTACT, payload: {...contact, subscription} })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    if (document.location.pathname.match(/^\/confirm$/)) {
      let urlDomain = getReturnUrlDomain()
      let urlPath = getReturnUrlPath()
      let url
      if (urlPath.match(/^https?:/i)) {
        url = urlPath;
      } else {
        if (urlDomain.endsWith('/')) urlDomain = urlDomain.slice(0, urlDomain.length - 1)
        if (urlDomain && urlPath && !urlPath.startsWith('/')) urlPath = '/' + urlPath
        url = `${urlDomain}${urlPath}`
      }
      // negative look behind not available in Safari
      // const url = `${getReturnUrlDomain()}/${getReturnUrlPath()}`.replace(/(?<!https?:)\/\//g, '/') // remove concat that causes double slash in URL
      confirmErrorHandler(error, url)
    }
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contact'} })
  }
}

function * updateContact (action) {
  try {
    yield put({ type: SET_CONTACT, payload: action.payload.contact })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contact'} })
  }
}

function * saveContact (action) {
  try {
    yield call(putData, `contacts/${action.payload.contact.emailAddress}`, action.payload.contact)
    yield put({ type: SET_CONTACT, payload: action.payload.contact })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contact'} })
  }
}

function * confirmProfile (action) {
  const { contact, contactIsChanged } = action.payload
  let isConfirmed = true
  if (contactIsChanged) {
    try {
      yield call(putData, `contacts/${contact.emailAddress}`, contact)
    } catch (error) {
      if (settings.isProduction) Raven.captureException(serializeError(error))
      console.error(error)
      isConfirmed = false
    }
  }

  const domain = getReturnUrlDomain()
  const returnUrl = getReturnUrlPath()
  const actionUrl = domain.match(/localhost/) ? 'Login/Profile' : 'User/Login/Profile'
  try {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `${domain}/${actionUrl}`

    const iReturnUrl = document.createElement('input')
    iReturnUrl.name = 'ReturnUrl'
    iReturnUrl.value = returnUrl
    form.appendChild(iReturnUrl)

    const iConfirm = document.createElement('input')
    iConfirm.name = 'ProfileConfirmed'
    iConfirm.value = isConfirmed
    form.appendChild(iConfirm)

    document.body.appendChild(form)
    form.submit()
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    confirmErrorHandler(error, `${domain}/${returnUrl}`)
  }
}

function * saveSubscription (action) {
  try {
    yield call(putData, `subscriptions/${action.payload.subscriptionUpdates.emailAddress}`, action.payload.subscriptionUpdates)
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'subscription'} })
  }
}

function * fetchContacts (action) {
  try {
    const contacts = yield call(getData, `contacts?emailAddress=${action.payload.emailAddress}`)
    yield put({ type: SET_CONTACTS, payload: contacts })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contacts'} })
  }
}

// 2018-11-02: doesn't look to be used
function * updateContacts (action) {
  try {
    yield put({ type: SET_CONTACTS, payload: action.payload.contacts })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contacts'} })
  }
}

function * saveDeactivate (action) {
  try {
    yield call(deleteData, `contacts/${action.payload.deactivateRequest.emailAddress}`, action.payload.deactivateRequest)
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contacts'} })
  }
}

function * saveEmail (action) {
  try {
    yield call(postData, `contacts/${action.payload.emailChangeRequest.emailAddress}/email`, action.payload.emailChangeRequest)
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contacts'} })
  }
}

function * addContacts (action) {
  try {
    yield call(postData, `contacts/${action.payload.addContactsRequest.emailAddress}/new`, action.payload.addContactsRequest)
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contacts'} })
  }
}

export function * fetchExclusions (action) {
  try {
    const response = yield call(getData, `publication/contact/schema/filter/${action.payload.emailAddress}`, true)
    // this is the same call as the fetchEmailPreferences, but we carved them up in the store
    // when we go to save, if email_preferences is there then it'll overwrite with old values
    const exclusions = {
      filter_groups: response.filter_groups,
      filter_preferred: response.filter_preferred,
      suspend_publications: response.suspend_publications
    }
    yield put({ type: SET_EXCLUSIONS, payload: exclusions })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'exclusions'} })
  }
}

function * saveExclusions (action) {
  try {
    yield call(postData, `publication/contact/schema/filter/${action.payload.emailAddress}`, action.payload.exclusions, true)
    yield put({ type: SET_EXCLUSIONS, payload: action.payload.exclusions })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'exclusions'} })
  }
}

function * fetchFilterResults (action) {
  const user = yield select(getUserInfo)
  if (!user || !user.emailPreferences || !user.emailPreferences.preferences) return

  const preference = user.emailPreferences.preferences.filter(x => x.name)[action.payload.filterIndex]
  try {
    const proxyQueryResultsResponse = yield call(
      fetchJSON, 
      `${settings.API_IPD_ROUTE_USER}/Pharma/UserPreferences/${action.payload.emailAddress}/QueryResults`, 
      {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify([ preference ])
      }
    )

    yield put({
      type: SET_FILTER_RESULTS,
      payload: {
        filterIndex: action.payload.filterIndex,
        result: proxyQueryResultsResponse.filters[0]
      }
    })

    return proxyQueryResultsResponse.filters[0]
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'filterResults'} })
  }
}

export function * fetchEmailPreferences (action) {
  const user = yield select(getUserInfo)
  if (!user || !user.contact) yield fetchContact({ payload: { emailAddress: action.payload.emailAddress } })

  try {
    const lambdaResponse = yield call(getData, `publication/contact/schema/filter/${action.payload.emailAddress}`, true)

    // lambdaResponse also carries the exclusions data (same schema/filter endpoint as fetchExclusions), so set it here to avoid a duplicate request
    yield put({
      type: SET_EXCLUSIONS,
      payload: {
        filter_groups: (lambdaResponse || {}).filter_groups,
        filter_preferred: (lambdaResponse || {}).filter_preferred,
        suspend_publications: (lambdaResponse || {}).suspend_publications
      }
    })

    const [ proxyQueryOptionsResponse, proxyQueryResultsResponse ] = yield all([
      call(fetchJSON, `${settings.API_IPD_ROUTE_USER}/Pharma/UserPreferences/QueryOptions`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include'
      }),
      call(fetchJSON, `${settings.API_IPD_ROUTE_USER}/Pharma/UserPreferences/${action.payload.emailAddress}/QueryResults`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify([])
      })
    ])

    const preferences = (lambdaResponse || {}).email_preferences
    const categories = (proxyQueryOptionsResponse || {}).categories
    if (isEmpty(preferences)) {
      yield saveEmailPreferences({
        payload: {
          emailAddress: action.payload.emailAddress,
          emailPreferences: {
            preferences,
            categories
          }
        }
      })
    } else {
      yield put({
        type: SET_EMAIL_PREFERENCES,
        payload: {
          categories: categories,
          preferences: preferences,
          originalPreferences: preferences,
          filterResults: { ...proxyQueryResultsResponse, filters: lambdaResponse.email_preferences.map(x => null) } // pre-allocate array so indexes work
        }
      })
    }
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'emailPreferences'} })
  }
}

function * updateEmailPreferences (action) {
  try {
    yield put({ type: SET_EMAIL_PREFERENCES, payload: action.payload.emailPreferences })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'contact'} })
  }
}

function * saveEmailPreferences (action) {
  try {
    let {categories, preferences, savedPreferenceName} = action.payload.emailPreferences
    if (isEmpty(preferences)) {
      preferences = [DEFAULT_EMAIL_PREFERENCE]
    }

    yield call(postData, `publication/contact/schema/filter/${action.payload.emailAddress}`, {email_preferences: preferences}, true)

    const emailPreferences = {
      ...yield select(state => getUserInfo(state).emailPreferences),
      categories: categories,
      preferences: preferences,
      originalPreferences: preferences,
      preferenceWithEmptyResults: undefined
    }
    yield put({type: SET_EMAIL_PREFERENCES, payload: emailPreferences}) // save first so fetchFilterResults can get it

    const savedPreference = preferences.find(x => x.name === savedPreferenceName)
    if (savedPreference && savedPreference.name && savedPreference.active && !isEmpty(savedPreference.filters)) {
      const filterResults = yield fetchFilterResults({ payload: { emailAddress: action.payload.emailAddress, filterIndex: preferences.indexOf(savedPreference) } })
      if (isEmpty(filterResults.values)) {
        // save again to set empty results
        yield put({type: SET_EMAIL_PREFERENCES, payload: { ...yield select(state => getUserInfo(state).emailPreferences), preferenceWithEmptyResults: savedPreference }}) // save first so fetchFilterResults can get it
      }
    }
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'emailPreferences'} })
  }
}

function * fetchDatabasePreferences (action) {
  try {
    const response = yield call(
      fetchJSON,
      `${settings.API_IPD_ROUTE_USER}/Pharma/UserPreferences/${action.payload.emailAddress}`,
      {
        method: 'GET',
        mode: 'cors',
        credentials: 'include'
      }
    )
    yield put({ type: SET_DATABASE_PREFERENCES, payload: response })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'databasePreferences'} })
  }
}

function * saveDatabasePreferences (action) {
  try {
    yield call(
      fetchJSON,
      action.payload.url,
      {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          id: action.payload.id
        })
      }
    )
    yield put({ type: SET_DATABASE_PREFERENCES, payload: action.payload.preferences })
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'databasePreferences'} })
  }
}

function * dismissFirstTimeHelp (action) {
  const interstitial = yield select(state => getUserInfo(state).databasePreferences.interstitial)
  const link = interstitial.links.find(x => x.rel === 'dismiss')
  try {
    yield call(
      fetchJSON,
      link.href,
      {
        method: link.method,
        mode: 'cors',
        credentials: 'include',
        headers: {
          'content-type': 'application/json'
        }
      }
    )
  } catch (error) {
    if (settings.isProduction) Raven.captureException(serializeError(error))
    console.error(error)
    yield put({ type: SET_ERROR, payload: {error, errorType: 'dismissFirstTimeHelp'} })
  }
}

function * rootSaga () {
  yield all([
    takeLatest(INIT, authorize),
    takeLatest(LOCATION_CHANGE, authorize),
    takeLatest(FETCH_CONTACT, fetchContact),
    takeLatest(UPDATE_CONTACT, updateContact),
    takeLatest(SAVE_CONTACT, saveContact),
    takeLatest(SAVE_SUBSCRIPTION, saveSubscription),
    takeLatest(FETCH_CONTACTS, fetchContacts),
    takeLatest(UPDATE_CONTACTS, updateContacts), // 2018-11-02: doesn't look to be used
    takeLatest(SAVE_DEACTIVATE, saveDeactivate),
    takeLatest(SAVE_EMAIL, saveEmail),
    takeLatest(ADD_CONTACTS, addContacts),
    takeLatest(CONFIRM_PROFILE, confirmProfile),
    takeLatest(FETCH_EXCLUSIONS, fetchExclusions),
    takeLatest(SAVE_EXCLUSIONS, saveExclusions),
    takeLatest(FETCH_EMAIL_PREFERENCES, fetchEmailPreferences),
    takeLatest(UPDATE_EMAIL_PREFERENCES, updateEmailPreferences),
    takeLatest(SAVE_EMAIL_PREFERENCES, saveEmailPreferences),
    takeLatest(FETCH_DATABASE_PREFERENCES, fetchDatabasePreferences),
    takeLatest(SAVE_DATABASE_PREFERENCES, saveDatabasePreferences),
    takeEvery(FETCH_FILTER_RESULTS, fetchFilterResults),
    takeLatest(DISMISS_FIRST_TIME_HELP, dismissFirstTimeHelp)
  ])
}

export default rootSaga
